// src/components/mobile/GroupIdentityCard.jsx
//
// Persistent identity card for the mobile Group front page (Approach B, Mobile Groups tier).
// Sits ABOVE the content tabs and stays visible across them: group logo · name · "…more"
// (opens the group intro / group-landing-view) · membership status line.
//
// Sub-groups / part-of / membership actions live on the Home tab (GroupHomePanel) — the old
// "Group" button + GroupSheet were removed (Tier 2 addendum) once the Home tab subsumed them.
//
// Presentational only — all membership/hierarchy state arrives via the `home` object
// (a useGroupHome return), owned by MobileGroupFrontPage so the hook runs once. No fetches,
// no service calls here. Mauve via theme.primary (fallback tokens.colors.contextGroups —
// never a hardcoded mauve).

import React from 'react';
import { Users } from 'lucide-react';
import { useTheme } from '../../themes/ThemeProvider';
import { useTranslation } from '../../i18n/useTranslation';
import { tokens } from '../../styles/designTokens';
import { groupTypeLabelKey } from '../../utils/groupHelpers';

const GroupIdentityCard = ({ group, home }) => {
  const theme = useTheme();
  const { t } = useTranslation('groups');
  const mauve = theme?.primary || tokens.colors.contextGroups;

  if (!group) return null;

  const ms = home?.membershipStatus || {};

  const openIntro = () => {
    window.dispatchEvent(new CustomEvent('ournet:navigate', {
      detail: { view: 'group-landing-view', group }
    }));
  };

  // Status line — reuses existing membership keys (no roster exposure here).
  const statusText = () => {
    if (ms.loading) return '';
    if (ms.isOwner) return t('ui:owner_badge') || 'Owner';
    if (ms.isMember) return t('member_badge') || 'Member';
    if (ms.hasPendingRequest) return t('ui:request_pending') || 'Request pending';
    return t(groupTypeLabelKey(group));
  };
  const statusColor = ms.isMember ? '#5DBEA3' : (ms.hasPendingRequest ? '#F59E0B' : tokens.colors.textSecondary);

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: '10px 12px',
      background: '#FFFFFF',
      fontFamily: tokens.typography.fontFamily
    }}>
      {/* Logo */}
      <div style={{
        width: '56px',
        height: '42px',
        flexShrink: 0,
        borderRadius: '8px',
        overflow: 'hidden',
        background: `linear-gradient(135deg, ${mauve}cc, ${mauve})`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        {group.logo_url ? (
          <img
            src={group.logo_url}
            alt={group.name}
            loading="lazy"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        ) : (
          <Users size={20} color="#fff" strokeWidth={2} />
        )}
      </div>

      {/* Name · status · "…more" */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: '15px',
          fontWeight: 'bold',
          color: mauve,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}>
          {group.name}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '1px' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: statusColor }}>
            <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: statusColor, flexShrink: 0 }} />
            {statusText()}
          </span>
          <button
            onClick={openIntro}
            style={{
              background: 'none', border: 'none', padding: 0, cursor: 'pointer',
              color: mauve, fontWeight: 'bold', fontSize: '11px',
              fontFamily: tokens.typography.fontFamily
            }}
          >
            {t('more_link') || '…more'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GroupIdentityCard;
